
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { UseFormReturn } from "react-hook-form";
import { FilmFormValues } from "@/types/inventory";
import { useTranslation } from "@/hooks/useTranslation";
import { useState, useEffect } from "react";
import DOMPurify from "dompurify";

interface FilmNotesFieldProps {
  form: UseFormReturn<FilmFormValues>;
}

export function FilmNotesField({ form }: FilmNotesFieldProps) {
  const { t } = useTranslation();
  const [charCount, setCharCount] = useState(form.getValues("notes")?.length || 0);
  const [noteError, setNoteError] = useState<string | null>(null);
  const maxChars = 100;

  // Validate notes for any malicious content
  const validateNotes = (value: string) => {
    if (!value) {
      setNoteError(null);
      return true;
    }

    // Check for potential XSS or script content
    const sanitized = DOMPurify.sanitize(value);
    if (sanitized !== value) {
      setNoteError("Notes contain potentially unsafe content");
      return false;
    }

    // Check for specific patterns that might indicate script injection
    const maliciousPatterns = [
      /<script/i,
      /javascript:/i,
      /onclick/i,
      /onerror/i,
      /eval\(/i
    ];

    for (const pattern of maliciousPatterns) {
      if (pattern.test(value)) {
        setNoteError("Notes contain potentially unsafe content");
        return false;
      }
    }

    // Check character limit
    if (value.length > maxChars) {
      setNoteError(`Notes cannot exceed ${maxChars} characters`);
      return false;
    }

    setNoteError(null);
    return true;
  };

  // Initialize validation on mount
  useEffect(() => {
    const initialValue = form.getValues("notes");
    if (initialValue) {
      setCharCount(initialValue.length);
      validateNotes(initialValue);
    }
  }, []);

  return (
    <FormField
      control={form.control}
      name="notes"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t('notes')}</FormLabel>
          <FormControl>
            <Textarea 
              placeholder={t('notes_placeholder')} 
              className={`resize-none ${noteError ? "border-destructive" : ""}`} 
              rows={3} 
              {...field} 
              value={field.value || ""}
              maxLength={maxChars}
              onChange={(e) => {
                const value = e.target.value;
                field.onChange(value);
                setCharCount(value.length);
                validateNotes(value);
              }}
              onBlur={(e) => validateNotes(e.target.value)}
            />
          </FormControl>
          <div className={`text-xs ${charCount > maxChars ? "text-destructive" : "text-muted-foreground"} text-right mt-1`}>
            {charCount}/{maxChars}
          </div>
          {noteError && (
            <p className="text-xs font-medium text-destructive">{noteError}</p>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
